/**
 * System status (trading pause, mode) from /api/system/status.
 * Fetches on mount and polls every 30s. Default: tradingPaused false.
 * Only set tradingPaused true when API explicitly returns { tradingPaused: true }.
 * On API error/timeout: keep previous state. 3-retry logic before showing paused.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { apiRequest } from "../api/apiClient";

const POLL_INTERVAL_MS = 30000;
const MAX_RETRIES = 3;

const SystemStatusContext = createContext({
  tradingPaused: false,
  mode: "NORMAL",
  loading: true,
  refresh: async () => {},
});

export function SystemStatusProvider({ children }) {
  const [state, setState] = useState({
    tradingPaused: false,
    mode: "NORMAL",
    loading: true,
  });
  const retryCountRef = useRef(0);

  const fetchStatus = useCallback(async () => {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const data = await apiRequest("/api/system/status", { method: "GET" });
        retryCountRef.current = 0;
        setState((prev) => ({
          tradingPaused: data != null && data.tradingPaused === true,
          mode: data != null && data.mode ? data.mode : (prev.mode ?? "NORMAL"),
          loading: false,
        }));
        return;
      } catch {
        if (attempt === MAX_RETRIES - 1) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      }
    }
    retryCountRef.current = 0;
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return (
    <SystemStatusContext.Provider
      value={{
        tradingPaused: state.tradingPaused,
        mode: state.mode,
        loading: state.loading,
        refresh: fetchStatus,
      }}
    >
      {children}
    </SystemStatusContext.Provider>
  );
}

export const useSystemStatus = () => useContext(SystemStatusContext);
